import type { SelectItemType } from '@/components/base/select/select';

import {
  EducationLevel,
  EmploymentStatus,
  Ethnicities,
  Gender,
  HouseholdIncome,
  SeniorityLevel,
} from '@/api/core-api';
import {
  getEducationLevelLabel,
  getEmploymentStatusLabel,
  getEthnicityLabel,
  getGenderLabel,
  getHouseholdIncomeLabel,
  getSeniorityLevelLabel,
} from '@/lib/matching-attributes/labels';

function toSelectOptions<T extends string>(
  values: readonly T[],
  getLabel: (value: T) => string,
): SelectItemType<T>[] {
  return values.map((id) => ({ id, label: getLabel(id) }));
}

const GENDER_ORDER: Gender[] = [
  Gender.MALE,
  Gender.FEMALE,
  Gender.NONBINARY,
  Gender.TRANSMALE,
  Gender.TRANSFEMALE,
  Gender.OTHER,
  Gender.PREFERNOTTOANSWER,
  Gender.N_A,
];

const ETHNICITY_ORDER: Ethnicities[] = [
  Ethnicities.AMERICANINDIANALASKANNATIVE,
  Ethnicities.HISPANICLATINO,
  Ethnicities.BLACKORAFRICANAMERICAN,
  Ethnicities.ASIANPACIFICISLANDER,
  Ethnicities.WHITECAUCASIAN,
  Ethnicities.MIDDLEEASTERN,
  Ethnicities.MULTIPLEETHNICITY,
  Ethnicities.PREFERNOTDISCLOSE,
  Ethnicities.N_A,
];

const HOUSEHOLD_INCOME_ORDER: HouseholdIncome[] = [
  HouseholdIncome['<30000'],
  HouseholdIncome['30000_39999'],
  HouseholdIncome['40000_59999'],
  HouseholdIncome['60000_79999'],
  HouseholdIncome['80000_99999'],
  HouseholdIncome['100000_149999'],
  HouseholdIncome['150000_199999'],
  HouseholdIncome[200_000],
];

const EMPLOYMENT_STATUS_ORDER: EmploymentStatus[] = [
  EmploymentStatus.EMPLOYEDFULLTIME,
  EmploymentStatus.EMPLOYEDPARTTIME,
  EmploymentStatus.OWNER,
  EmploymentStatus.FULLTIMESTUDENT,
  EmploymentStatus.PARTTIMESTUDENT,
  EmploymentStatus.HOMEMAKER,
  EmploymentStatus.UNEMPLOYEDLOOKINGFORWORK,
  EmploymentStatus.UNEMPLOYED,
  EmploymentStatus.RETIRED,
  EmploymentStatus.DISABLED,
];

const EDUCATION_LEVEL_ORDER: EducationLevel[] = [
  EducationLevel.SOMEHIGHSCHOOL,
  EducationLevel.HIGHSCHOOLGRADUATE,
  EducationLevel.SOMECOLLEGENODEGREE,
  EducationLevel.TRADETECHNICALVOCATIONALTRAINING,
  EducationLevel.ASSOCIATEDEGREE,
  EducationLevel.BACHELORDEGREE,
  EducationLevel.MASTERDEGREE,
  EducationLevel.DOCTORATEDEGREE,
  EducationLevel.N_A,
];

const SENIORITY_LEVEL_ORDER: SeniorityLevel[] = [
  SeniorityLevel.STUDENT,
  SeniorityLevel.TRAINING,
  SeniorityLevel.UNPAID,
  SeniorityLevel.ENTRYLEVEL,
  SeniorityLevel.SENIOR,
  SeniorityLevel.MANAGER,
  SeniorityLevel.DIRECTOR,
  SeniorityLevel.VICEPRESIDENT,
  SeniorityLevel.CHIEFOFFICER,
  SeniorityLevel.PARTNER,
];

export const genderOptions = toSelectOptions(GENDER_ORDER, getGenderLabel);

export const ethnicityOptions = toSelectOptions(
  ETHNICITY_ORDER,
  getEthnicityLabel,
);

export const householdIncomeOptions = toSelectOptions(
  HOUSEHOLD_INCOME_ORDER,
  getHouseholdIncomeLabel,
);

/** @deprecated Use `householdIncomeOptions` */
export const incomeOptions = householdIncomeOptions;

export const educationOptions = toSelectOptions(
  EDUCATION_LEVEL_ORDER,
  getEducationLevelLabel,
);

export const employmentStatusOptions = toSelectOptions(
  EMPLOYMENT_STATUS_ORDER,
  getEmploymentStatusLabel,
);

export const seniorityLevelOptions = toSelectOptions(
  SENIORITY_LEVEL_ORDER,
  getSeniorityLevelLabel,
);

/** Participant-facing options (excludes N/A sentinel values). */
export const participantGenderOptions = genderOptions.filter(
  (option) => option.id !== Gender.N_A,
);

export const participantEthnicityOptions = ethnicityOptions.filter(
  (option) => option.id !== Ethnicities.N_A,
);

export const participantEducationOptions = educationOptions.filter(
  (option) => option.id !== EducationLevel.N_A,
);
